import { type PropsWithChildren, useEffect } from 'react'

import type { CreatorPermission, FeatureFlagName } from '@api'
import { type MeInformation, TeamMemberRole } from '@api'
import { useUnit } from 'effector-react'
import { Outlet, useNavigate } from 'react-router'

import { useToast } from '@/components/ui/toast.tsx'
import { $account } from '@/models/account.ts'
import {
  $creators,
  $currentCreator,
  $isCreatorProcessCompleted,
} from '@/models/creators.ts'
import { $availableFeatures } from '@/models/feature-flags'
import type { Creator } from '@/modules/creators/types'
import { isActiveCreator } from '@/modules/creators/utils'
import { AppRoutes } from '@/utils/constants'
import { isFeatureAvailable as isFeatureAvailableUtil } from '@/utils/featureFlags'

import type { CreatorFeatures } from '../types'

interface AccessControlGuardProps extends PropsWithChildren {
  featureDisplayName: string
  creatorPermission?: CreatorPermission
  creatorFeature?: CreatorFeatures
  accountFeature?: CreatorFeatures
  creatorRequired?: boolean
  onlyOwner?: boolean
  onlyAdmin?: boolean
  featureFlagName?: FeatureFlagName
}

export const AccessControlGuard = ({
  featureDisplayName,
  children,
  creatorPermission,
  creatorFeature,
  accountFeature,
  creatorRequired,
  onlyOwner,
  onlyAdmin,
  featureFlagName,
}: AccessControlGuardProps) => {
  const toast = useToast()
  const creators = useUnit($creators)
  const activeCreator = useUnit($currentCreator)
  const isCreatorProcessCompleted = useUnit($isCreatorProcessCompleted)
  const account = useUnit($account)
  const navigate = useNavigate()
  const availableFeatures = useUnit($availableFeatures)

  const isFeatureAvailable = isFeatureAvailableUtil(
    availableFeatures,
    featureFlagName,
  )

  useEffect(() => {
    if (
      activeCreator &&
      creators &&
      (!isFeatureAvailable ||
        !hasPermissionsToRoute(
          activeCreator,
          creatorPermission,
          creatorFeature,
          account,
          accountFeature,
          creators,
          onlyOwner,
          onlyAdmin,
        ))
    ) {
      toast.error("You don't have the permission to access this page")
      navigate(AppRoutes.Home.asUrl(), { replace: true })

      return
    }

    if (
      isCreatorProcessCompleted &&
      creators &&
      account?.memberRole === TeamMemberRole.LEADER &&
      !hasRouteFeature(
        activeCreator,
        creatorFeature,
        accountFeature,
        creators,
        creatorRequired,
      )
    ) {
      if (activeCreator) {
        if (creatorFeature || accountFeature) {
          const searchParams = new URLSearchParams()

          searchParams.set(
            'upgradeFeature',
            (creatorFeature ? creatorFeature : accountFeature) as string,
          )
          searchParams.set('upgradeFeatureName', featureDisplayName)
          navigate(
            AppRoutes.Creators.List.asUrl() + '?' + searchParams.toString(),
            {
              replace: true,
            },
          )
        } else {
          toast.error('Please upgrade your plan to use this feature.')
          navigate(AppRoutes.Home.asUrl(), { replace: true })
        }

        return
      }

      toast.success('Please add your first creator to setup this feature.')
      const searchParams = new URLSearchParams()

      searchParams.set('addCreator', '1')
      navigate(
        AppRoutes.Creators.List.asUrl() + '?' + searchParams.toString(),
        {
          replace: true,
        },
      )

      return
    }
  }, [
    account,
    accountFeature,
    activeCreator,
    creatorFeature,
    creatorPermission,
    creators,
    navigate,
    onlyOwner,
    onlyAdmin,
    isCreatorProcessCompleted,
    toast,
    featureDisplayName,
    creatorRequired,
    isFeatureAvailable,
  ])

  return children || <Outlet />
}

export const hasPermissionsToRoute = (
  activeCreator: Creator | null,
  creatorPermission?: CreatorPermission,
  creatorFeature?: CreatorFeatures,
  account?: MeInformation | null,
  accountFeature?: CreatorFeatures,
  creators?: Creator[],
  onlyOwner?: boolean,
  onlyAdmin?: boolean,
) => {
  // Leader can generally access very page
  if (account?.memberRole === TeamMemberRole.LEADER) {
    return true
  }

  if (onlyOwner) {
    return false
  }

  if (onlyAdmin && account?.memberRole !== TeamMemberRole.ADMIN) {
    return false
  }

  // All the non-leader users should have the permission as well as access to the feature
  const userHasPermissions = !creatorPermission
    ? true
    : activeCreator && activeCreator.permissions.includes(creatorPermission)

  return (
    userHasPermissions &&
    hasRouteFeature(activeCreator, creatorFeature, accountFeature, creators)
  )
}

const hasRouteFeature = (
  activeCreator: Creator | null,
  creatorFeature?: CreatorFeatures,
  accountFeature?: CreatorFeatures,
  creators?: Creator[],
  creatorRequired?: boolean,
) => {
  const accountHasAccess = !accountFeature
    ? true
    : creators &&
      creators.find(
        (creator) =>
          isActiveCreator(creator) && creator.features.includes(accountFeature),
      )
  const creatorHasFeature = !creatorFeature
    ? true
    : !!activeCreator?.features.includes(creatorFeature)

  const creatorHasAccess = !creatorRequired || !!creators?.find(isActiveCreator)

  return creatorHasFeature && accountHasAccess && creatorHasAccess
}
